import { API_BASE } from "../config/api";
import {
  AddressFilters,
  CRMAddPointsRequest,
  CRMRedeemPointsRequest,
  Customer,
  CustomerAddress,
  CustomerAddressBulkDeleteRequest,
  CustomerAddressBulkUpdateStatusRequest,
  CustomerAddressCreateRequest,
  CustomerAddressListResponse,
  CustomerBulkUpdateStatusRequest,
  CustomerCreateRequest,
  CustomerECPShipTo,
  CustomerFilters,
  CustomerListResponse,
  CustomerMultipleUpdatePointsRequestData,
  CustomerSendCustomMessageRequest,
  CustomerUpdateRequest,
  DeleteRequest,
  FAQSettings,
  Privilege,
  PrivilegeCreateRequest,
  PrivilegeFilters,
  PrivilegeGetAllResponse,
  PrivilegeUpdate,
  Redemption,
  RedemptionFilters,
  RedemptionGetAllResponse,
  RedemptionUpdateRequest,
  UpdateCustomerAddressRequest,
} from "../types/crm";
import { authFetcher } from "../utils/auth-fetch";

const BASE = API_BASE.CUSTOMER_ADMIN;

// ============ Customer API ============
const getCustomers = async (params: CustomerFilters = {}) => {
  return await authFetcher<CustomerListResponse>(
    "GET",
    `${BASE}/customers`,
    null,
    { ...params },
  );
};

const getCustomer = async (id: string) => {
  return await authFetcher<Customer>("GET", `${BASE}/customers/${id}`);
};

const createCustomer = async (data: CustomerCreateRequest) => {
  return await authFetcher<Customer>("POST", `${BASE}/customers`, data);
};

const updateCustomer = async (id: string, data: CustomerUpdateRequest) => {
  return await authFetcher<Customer>("PATCH", `${BASE}/customers/${id}`, data);
};

const deleteCustomer = async (id: string, data?: DeleteRequest) => {
  return await authFetcher<void>("DELETE", `${BASE}/customers/${id}`, data);
};

const bulkUpdateCustomerStatus = async (
  data: CustomerBulkUpdateStatusRequest,
) => {
  return await authFetcher<void>(
    "PATCH",
    `${BASE}/customers/bulk-status`,
    data,
  );
};

const addCustomerPoints = async (id: string, data: CRMAddPointsRequest) => {
  return await authFetcher<Customer>(
    "POST",
    `${BASE}/customers/${id}/points/add`,
    data,
  );
};

const redeemCustomerPoints = async (
  id: string,
  data: CRMRedeemPointsRequest,
) => {
  return await authFetcher<Customer>(
    "POST",
    `${BASE}/customers/${id}/points/redeem`,
    data,
  );
};

const sendCustomerMessage = async (
  id: string,
  data: CustomerSendCustomMessageRequest,
) => {
  return await authFetcher<void>(
    "POST",
    `${BASE}/customers/${id}/send-message`,
    data,
  );
};

const sendMessageByEcpCode = async (
  ecpCode: string,
  data: { message: string },
) => {
  return await authFetcher<void>(
    "POST",
    `${BASE}/customers/ecp/${ecpCode}/send-message`,
    data,
  );
};

const multipleUpdatePoints = async (
  data: CustomerMultipleUpdatePointsRequestData[],
) => {
  return await authFetcher<void>(
    "POST",
    `${BASE}/customers/multiple-update-points`,
    { data },
  );
};

// ============ Address API ============
const getAddresses = async (params: AddressFilters = {}) => {
  return await authFetcher<CustomerAddressListResponse>(
    "GET",
    `${BASE}/addresses`,
    null,
    { ...params },
  );
};

const getAddress = async (id: string) => {
  return await authFetcher<CustomerAddress>("GET", `${BASE}/addresses/${id}`);
};

const getCustomerECPShipTo = async (customerId: string) => {
  return await authFetcher<CustomerECPShipTo[]>(
    "GET",
    `${BASE}/customers/${customerId}/ecp/ship-to`,
  );
};

const createAddress = async (
  data: CustomerAddressCreateRequest,
  ecpCode: string,
) => {
  return await authFetcher<CustomerAddress>(
    "POST",
    `${BASE}/addresses/${ecpCode}`,
    data,
  );
};

const updateAddress = async (
  id: string,
  data: UpdateCustomerAddressRequest,
) => {
  return await authFetcher<CustomerAddress>(
    "PATCH",
    `${BASE}/addresses/${id}`,
    data,
  );
};

const deleteAddress = async (id: string) => {
  return await authFetcher<void>("DELETE", `${BASE}/addresses/${id}`);
};

const bulkDeleteAddresses = async (data: CustomerAddressBulkDeleteRequest) => {
  return await authFetcher<void>(
    "DELETE",
    `${BASE}/addresses/bulk-delete`,
    data,
  );
};

const bulkUpdateAddressStatus = async (
  data: CustomerAddressBulkUpdateStatusRequest,
) => {
  return await authFetcher<void>(
    "PATCH",
    `${BASE}/addresses/bulk-status`,
    data,
  );
};

// ============ Privilege API ============
const getPrivileges = async (params: PrivilegeFilters = {}) => {
  return await authFetcher<PrivilegeGetAllResponse>(
    "GET",
    `${BASE}/privileges`,
    null,
    { ...params },
  );
};

const getPrivilege = async (id: string) => {
  return await authFetcher<Privilege>("GET", `${BASE}/privileges/${id}`);
};

const createPrivilege = async (data: PrivilegeCreateRequest) => {
  return await authFetcher<Privilege>("POST", `${BASE}/privileges`, data);
};

const updatePrivilege = async (id: string, data: PrivilegeUpdate) => {
  return await authFetcher<Privilege>(
    "PATCH",
    `${BASE}/privileges/${id}`,
    data,
  );
};

const deletePrivilege = async (id: string, data?: DeleteRequest) => {
  return await authFetcher<void>("DELETE", `${BASE}/privileges/${id}`, data);
};

// ============ Redemption API ============
const getRedemptions = async (params: RedemptionFilters = {}) => {
  return await authFetcher<RedemptionGetAllResponse>(
    "GET",
    `${BASE}/redemptions`,
    null,
    { ...params },
  );
};

const getRedemption = async (id: string) => {
  return await authFetcher<Redemption>("GET", `${BASE}/redemptions/${id}`);
};

const updateRedemption = async (id: string, data: RedemptionUpdateRequest) => {
  return await authFetcher<Redemption>(
    "PATCH",
    `${BASE}/redemptions/${id}`,
    data,
  );
};

const deleteRedemption = async (id: string, data?: DeleteRequest) => {
  return await authFetcher<void>("DELETE", `${BASE}/redemptions/${id}`, data);
};

// ============ FAQ API ============
const getFAQSettings = async () => {
  return await authFetcher<FAQSettings>("GET", `${BASE}/settings/faq`);
};

const updateFAQSettings = async (data: FAQSettings) => {
  return await authFetcher<FAQSettings>("PUT", `${BASE}/settings/faq`, data);
};

export {
  addCustomerPoints,
  bulkDeleteAddresses,
  bulkUpdateAddressStatus,
  bulkUpdateCustomerStatus,
  createAddress,
  createCustomer,
  createPrivilege,
  deleteAddress,
  deleteCustomer,
  deletePrivilege,
  deleteRedemption,
  getAddress,
  getFAQSettings,
  getAddresses,
  getCustomer,
  getCustomerECPShipTo,
  getCustomers,
  getPrivilege,
  getPrivileges,
  getRedemption,
  getRedemptions,
  multipleUpdatePoints,
  redeemCustomerPoints,
  sendCustomerMessage,
  sendMessageByEcpCode,
  updateAddress,
  updateCustomer,
  updateFAQSettings,
  updatePrivilege,
  updateRedemption,
};
